import type { Meta, StoryObj } from "@storybook/react";

import { fakeMultiSelectOptions } from "@/__mocks__/multiselect";
import type { FormPageProps } from "@/ui/composed/form/FormPage";
import FormPage from "@/ui/composed/form/FormPage";
import { FormFieldType } from "@/ui/composed/form/types";
import { ScreenContainer } from "@/ui/layout";

const StoryMeta: Meta<FormPageProps> = {
  title: "Composed/Form/Form Page",
  component: FormPage,
  argTypes: {
    groups: { control: "object" },
  },
  args: {
    groups: [
      {
        fields: [
          {
            hasError: false,
            leftText: "Legal first name",
            name: "legal-first-name",
          },
          {
            hasError: false,
            leftText: "Legal last name",
            name: "legal-last-name",
          },
          {
            hasError: false,
            leftText: "Preferred first name",
            name: "preferred-first-name",
          },
          {
            hasError: false,
            leftText: "Pronouns",
            name: "pronouns",
            options: fakeMultiSelectOptions(5),
          },
        ],
        heading: "Basic Info",
        onSave: (values) => console.log("Basic Info saved:", values),
        testID: "group-1",
        validationSchema: {
          "legal-first-name": { isRequired: true, type: FormFieldType.Text },
          "legal-last-name": { isRequired: true, type: FormFieldType.Text },
          "preferred-first-name": {
            isRequired: false,
            type: FormFieldType.Text,
          },
          pronouns: { isRequired: true, type: FormFieldType.Select },
        },
      },
      {
        fields: [
          {
            hasError: false,
            leftText: "Date of birth",
            name: "date-of-birth",
          },
          {
            hasError: false,
            leftText: "Ethnicity",
            name: "ethnicity",
            options: fakeMultiSelectOptions(8),
          },
          {
            hasError: false,
            leftText: "Sex assigned at birth",
            name: "sex",
            options: fakeMultiSelectOptions(3),
          },
          {
            hasError: false,
            leftText: "Gender",
            name: "gender",
            options: fakeMultiSelectOptions(7),
          },
        ],
        heading: "Demographic Info",
        onChange: (values) => console.log("Demographic Info changed:", values),
        onSave: (values) => console.log("Demographic Info saved:", values),
        testID: "group-2",
        validationSchema: {
          "date-of-birth": { isRequired: true, type: FormFieldType.Date },
          ethnicity: { isRequired: true, type: FormFieldType.Select },
          gender: { isRequired: true, type: FormFieldType.Select },
          sex: { isRequired: true, type: FormFieldType.Select },
        },
      },
      {
        fields: [
          {
            hasError: false,
            leftText: "Email address",
            name: "email-address",
          },
          {
            hasError: false,
            leftText: "Phone number",
            name: "phone-number",
          },
          {
            hasError: false,
            leftText: "Main address",
            name: "main-address",
          },
          {
            hasError: false,
            leftText: "This is also my shipping address",
            name: "is-shipping-address",
          },
          {
            hasError: false,
            leftText: "Preferred healthcare language",
            name: "language",
            options: fakeMultiSelectOptions(10),
            subLabel:
              "We’ll do our best to match you with providers who speak your preferred language when available.",
          },
        ],
        heading: "Contact Info",
        onChange: (values) => console.log("Contact Info changed:", values),
        onSave: (values) => console.log("Contact Info saved:", values),
        testID: "group-3",
        validationSchema: {
          "email-address": { isRequired: true, type: FormFieldType.Text },
          "is-shipping-address": {
            isRequired: false,
            type: FormFieldType.CheckBox,
          },
          language: { isRequired: true, type: FormFieldType.Select },
          "main-address": { isRequired: true, type: FormFieldType.Address },
          "phone-number": { isRequired: true, type: FormFieldType.Text },
        },
      },
      {
        fields: [
          {
            hasError: false,
            leftText: "Push notifications",
            name: "push-notifications",
            rightText: "Recommended",
            subLabel:
              "Notifications may include alerts, sounds, and icon badges. These can be configured in your iOS Settings for the Equip Health app.",
          },
        ],
        testID: "group-4",
        validationSchema: {
          "push-notifications": {
            isRequired: false,
            type: FormFieldType.CheckBox,
          },
        },
      },
      {
        fields: [
          {
            hasError: false,
            leftText: "Text messages",
            name: "text-messages",
            rightText: "Recommended",
            subLabel:
              "By checking this box, you agree to receive text messages from Equip Health. To cancel, reply STOP or change your app preferences. Msg & data rates may apply.",
          },
        ],
        testID: "group-5",
        validationSchema: {
          "text-messages": {
            isRequired: false,
            type: FormFieldType.CheckBox,
          },
        },
      },
      {
        fields: [
          {
            hasError: false,
            leftText: "Email",
            name: "email-notifications",
          },
        ],
        testID: "group-6",
        validationSchema: {
          "email-notifications": {
            isRequired: false,
            type: FormFieldType.CheckBox,
          },
        },
      },
    ],
  },
  render: (args) => (
    <ScreenContainer isScrollView>
      <FormPage {...args} />
    </ScreenContainer>
  ),
};

type Story = StoryObj<FormPageProps>;

const Primary: Story = {};

export { Primary };
export default StoryMeta;
